import { Table } from '../../../../components/table/table';
import { Translation } from '../../../internationalization/types/translation';
import { TextInputSmall } from '../../../../components/forms/input';
import { useQuery } from '../../../../hooks/use-query';
import { FieldValues, useForm } from 'react-hook-form';
import { usePostMutation, usePutMutation } from '../../../../hooks/use-mutations';
import { KeyValue } from '../../../../types/key-value';
import { useParams } from 'react-router';
import React, { useEffect, useState } from 'react';
import { Language } from '../../../internationalization/types/language';
import { GearIconColumn } from '../../../../components/table/columns';
import { useDefaultLanguage } from '../../../../hooks/use-default-language';

type MapLayerTranslation = {
  value?: string;
  description?: string;
  langCode?: string;
};

type MapLayerSavedTranslation = {
  id?: string;
  name?: string;
  description?: string;
  layerId?: string;
  lang: string;
}

type Props = {
  layerId?: string;
};

export const Translations = (props: Props): JSX.Element => {
  const params = useParams();
  const layerId = (props.layerId || params.id) as string;
  const { register, handleSubmit: handleTranslationSubmit, setValue } = useForm();
  const { handleSubmit } = useForm();
  const [nameValues, setNameValues] = useState<Translation[]>([]);
  const [descriptionValues, setDescriptionValues] = useState<Translation[]>([]);
  const languages = useQuery<Language[]>({ path: '/languages/all' });
  const defaultLang = useDefaultLanguage();
  const postMutationBatch = usePostMutation<Translation[]>({ path: '/translation' });
  const putMutationBatch = usePutMutation<MapLayerTranslation[]>({ path: `/map-layers/${layerId}/translations` });
  const translations = useQuery<MapLayerSavedTranslation[]>({ path: `/map-layers/${layerId}/translations` });

  useEffect(() => {
    if (translations.isSuccess && languages.isSuccess) {
      setExistingTranslations();
    }
  }, [translations.isSuccess, languages.isSuccess]);

  const columns = [
    ...GearIconColumn(),
    { accessor: 'name', Header: 'Language', sort: true },
    {
      accessor: 'value',
      Header: 'Name',
      Cell: (item: any): JSX.Element => (
        <TextInputSmall
          {...register(`values[${item.row.original.code as string}`, { required: item.row.original.code === defaultLang })}
        />
      )
    },
    { accessor: 'description',
      Header: 'Description',
      sort: true,
      Cell: (item: any): JSX.Element => (
        <TextInputSmall
          {...register(`descriptions[${item.row.original.code as string}`, { required: item.row.original.code === defaultLang })}
        />
      )
    },
  ];

  const createValueTranslations = (data: KeyValue): KeyValue[] => {
    return Object.entries(data)
      .filter(([key]) => key !== defaultLang)
      .map(([key]): Translation => {
        return {
          'key': key,
          'from': defaultLang,
          'to': key,
          'phrase': data[defaultLang],
        };
      });
  };

  const onTranslate = async (data: FieldValues): Promise<void> => {
    const valuesToTranslate = createValueTranslations(data.values as KeyValue);
    const descriptionsToTranslate = createValueTranslations(data.descriptions as KeyValue);
    try {
      const [values, descriptions] = await Promise.all([
        valuesToTranslate?.length ? postMutationBatch.mutateAsync(valuesToTranslate) : [],
        descriptionsToTranslate?.length ? postMutationBatch.mutateAsync(descriptionsToTranslate) : []
      ]);
      values?.forEach?.((item: Translation) => {
        setValue(`values[${item.key}]`, item.translation);
      });
      descriptions?.forEach?.((item: Translation) => {
        setValue(`descriptions[${item.key}]`, item.translation);
      });
      setNameValues(values);
      setDescriptionValues(descriptions);
    } catch(exception) {
      console.error('Exception', exception);
    }
  };

  const saveTranslations = async (): Promise<void> => {
    const translations: { [key: string]: MapLayerTranslation } = {};
    nameValues.forEach(item => {
      translations[item.to] = { value: item.translation, langCode: item.to };
    });
    descriptionValues.forEach(item => {
      translations[item.to] = { ...translations[item.to], description: item.translation };
    });
    translations[defaultLang] = {
      langCode: defaultLang,
      value: nameValues[0]?.phrase,
      description: descriptionValues[0]?.phrase
    };
    await putMutationBatch.mutateAsync(Object.values(translations));
  };

  const setExistingTranslations = (): void => {
    translations?.data?.forEach(item => {
      const language = languages?.data?.find((language: Language) => language.code === item.lang);
      if (language) {
        const code = language.code;
        setValue(`values[${code}]`, item.name);
        setValue(`descriptions[${code}]`, item.description);
      }
    });
  };

  return <>
    <form id="current-form" onSubmit={handleSubmit(saveTranslations)} />
    <form id="translations-form" onSubmit={handleTranslationSubmit(onTranslate)}>
      <Table isLoading={languages.isLoading} columns={columns} rows={languages.data || []} />
    </form>
  </>;
};
